import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../../../shared/store';
import { fetchTrace } from './traceability-api';
import { initialTraceabilityState, TraceabilityState } from './traceability-models';

export const fetchTraceability = createAsyncThunk(
    'traceability/fetchTrace',
    async ({ patientId, measureCmsId, year }: { patientId: number; measureCmsId: string; year: number }) => {
        const envelope = await fetchTrace(patientId, measureCmsId, year);
        if (envelope.succeeded && envelope.entity) {
            return envelope.entity;
        }
        throw new Error(envelope.message || 'Failed to load the trace.');
    }
);

const slice = createSlice({
    name: 'traceability',
    initialState: initialTraceabilityState,
    reducers: {
        clearTraceability: (state: TraceabilityState) => {
            state.trace = null;
            state.errorMessage = null;
            // Orphan any in-flight fetch so its late response can't repopulate a cleared view.
            state.currentRequestId = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTraceability.pending, (state: TraceabilityState, action) => {
                state.loading = true;
                state.errorMessage = null;
                state.currentRequestId = action.meta.requestId;
            })
            // requestId guard: a slow response for a previous tuple must not overwrite the
            // newest selection just because it arrives last.
            .addCase(fetchTraceability.fulfilled, (state: TraceabilityState, action) => {
                if (action.meta.requestId !== state.currentRequestId) return;
                state.loading = false;
                state.trace = action.payload;
            })
            .addCase(fetchTraceability.rejected, (state: TraceabilityState, action) => {
                if (action.meta.requestId !== state.currentRequestId) return;
                state.loading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the trace.';
            });
    },
});

export const { clearTraceability } = slice.actions;

export const selectTrace = (state: RootState) => (state as any).traceability?.trace ?? null;
export const selectTraceLoading = (state: RootState) => (state as any).traceability?.loading ?? false;
export const selectTraceError = (state: RootState) => (state as any).traceability?.errorMessage ?? null;

export default slice.reducer;
